/**
 * IndexedDB-based offline storage service
 * Caches data for offline viewing
 */

const DB_NAME = 'MyVenueMateOffline';
const DB_VERSION = 1;

const STORES = {
  BOOKINGS: 'bookings',
  LEADS: 'leads',
  INVENTORY: 'inventory',
  CALENDAR: 'calendar',
  DASHBOARD: 'dashboard',
  USER_CREDENTIALS: 'userCredentials',
  USER_DATA: 'userData'
};

let db = null;

/**
 * Initialize IndexedDB
 */
const initDB = () => {
  return new Promise((resolve, reject) => {
    if (db) {
      resolve(db);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error('Failed to open IndexedDB:', request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      db = request.result;
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = event.target.result;

      // Create object stores for each data type
      Object.values(STORES).forEach(storeName => {
        if (!database.objectStoreNames.contains(storeName)) {
          database.createObjectStore(storeName, { keyPath: 'id' });
        }
      });
    };
  });
};

/**
 * Save data to IndexedDB
 * @param {string} storeName - Name of the store
 * @param {string} id - Unique identifier for the data
 * @param {any} data - Data to store
 */
export const saveOfflineData = async (storeName, id, data) => {
  try {
    const database = await initDB();
    const transaction = database.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    
    const record = {
      id,
      data,
      timestamp: Date.now()
    };
    
    store.put(record);
    
    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => {
        resolve(true);
      };
      transaction.onerror = () => {
        console.error(`Failed to cache ${storeName}/${id}:`, transaction.error);
        reject(transaction.error);
      };
    });
  } catch (error) {
    console.error('Error saving offline data:', error);
    return false;
  }
};

/**
 * Get data from IndexedDB
 * @param {string} storeName - Name of the store
 * @param {string} id - Unique identifier for the data
 */
export const getOfflineData = async (storeName, id) => {
  try {
    const database = await initDB();
    const transaction = database.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.get(id);

    return new Promise((resolve, reject) => {
      request.onsuccess = () => {
        const result = request.result;
        if (result) {
          resolve(result.data);
        } else {
          resolve(null);
        }
      };
      request.onerror = () => {
        console.error(`Failed to get ${storeName}/${id}:`, request.error);
        reject(request.error);
      };
    });
  } catch (error) {
    console.error('Error getting offline data:', error);
    return null;
  }
};

/**
 * Clear all offline data
 */
export const clearOfflineData = async () => {
  try {
    const database = await initDB();
    
    Object.values(STORES).forEach(storeName => {
      const transaction = database.transaction(storeName, 'readwrite');
      const store = transaction.objectStore(storeName);
      store.clear();
    });
    
    return true;
  } catch (error) {
    console.error('Error clearing offline data:', error);
    return false;
  }
};

/**
 * Save user credentials for offline login (hashed)
 * @param {string} email 
 * @param {string} passwordHash - Already hashed password
 * @param {object} userData - User data to cache
 */
export const saveUserForOffline = async (email, passwordHash, userData) => {
  try {
    await saveOfflineData(STORES.USER_CREDENTIALS, email.toLowerCase(), {
      email: email.toLowerCase(),
      passwordHash,
      savedAt: Date.now()
    });
    
    await saveOfflineData(STORES.USER_DATA, email.toLowerCase(), userData);
    
    return true;
  } catch (error) {
    console.error('Error saving user for offline:', error);
    return false;
  }
};

/**
 * Get cached user credentials
 * @param {string} email 
 */
export const getOfflineUserCredentials = async (email) => {
  return getOfflineData(STORES.USER_CREDENTIALS, email.toLowerCase());
};

/**
 * Get cached user data
 * @param {string} email 
 */
export const getOfflineUserData = async (email) => {
  return getOfflineData(STORES.USER_DATA, email.toLowerCase());
};

/**
 * Simple hash function for password comparison (not cryptographically secure, but sufficient for offline check)
 * @param {string} str 
 */
export const simpleHash = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return hash.toString(36);
};

// Export store names for use in other files
export const OFFLINE_STORES = STORES;

export default {
  saveOfflineData,
  getOfflineData,
  clearOfflineData,
  saveUserForOffline,
  getOfflineUserCredentials,
  getOfflineUserData,
  simpleHash,
  OFFLINE_STORES: STORES
};
